import React from 'react';
import { ArrowLeft, BarChart3, Target, Clock, TrendingUp, AlertTriangle, Calendar } from 'lucide-react';
import AuthorCredit from './AuthorCredit';

const CategorySelector = ({ 
  selectedCategory, 
  selectedChartType, 
  onCategoryChange, 
  onChartTypeChange,
  onBack,
  isAuthenticated = true
}) => {
  // Definição das categorias e seus tipos de gráfico
  const categories = {
    'gestao-fluxo': {
      label: 'Gestão de Fluxo',
      icon: BarChart3,
      description: 'Métricas focadas no fluxo de trabalho e processo',
      chartTypes: [
        { key: 'leadtime', label: 'Lead Time', icon: Clock, description: 'Tempo total do item no sistema' },
        { key: 'throughput', label: 'Throughput', icon: TrendingUp, description: 'Itens entregues por período' },
        { key: 'bloqueios', label: 'Bloqueios', icon: AlertTriangle, description: 'Análise de impedimentos' }
      ]
    },
    'gestao-produto': {
      label: 'Gestão de Produto',
      icon: Target,
      description: 'Planeje e acompanhe Roadmaps e Cronogramas do seu produto.',
      chartTypes: [
        { key: 'roadmap', label: 'Roadmaps e Cronogramas', icon: Calendar, description: 'Construa roadmaps visuais e cronogramas detalhados' }
      ]
    }
  };

  // Se nenhuma categoria foi selecionada, mostrar seleção de categorias
  if (!selectedCategory) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex flex-col justify-between">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" key="categories-screen">
            {/* Título principal para área não logada - apenas na tela inicial */}
            {!isAuthenticated && (
              <div className="text-center py-4 mb-1">
                <h1 className="text-5xl font-bold text-blue-600 mb-2">
                  Dash Simples
                </h1>
                <p className="text-xl text-gray-600 mb-4">
                  Transforme seus dados em insights poderosos.
                </p>
              </div>
            )}
            
            {/* Header */}
            <div className="text-center mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Escolha sua área de análise</h1>
          {isAuthenticated && (
            <p className="text-gray-600 max-w-2xl mx-auto text-sm">Selecione entre gestão de fluxo ou gestão de produto</p>
          )}
        </div>

        {/* Grid de categorias */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {Object.entries(categories).map(([categoryKey, category]) => {
            const CategoryIcon = category.icon;
            
            return (
              <button
                key={categoryKey}
                onClick={() => onCategoryChange(categoryKey)}
                className="group relative bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-200 hover:border-blue-300 text-left"
              >
                {/* Ícone e Título na mesma linha */}
                <div className="mb-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                      <CategoryIcon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  
                  {/* Título ao lado do ícone */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {category.label}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {category.description}
                  </p>

                  {/* Preview dos tipos de gráfico */}
                  <div className="flex flex-wrap gap-2">
                    {category.chartTypes.map((chart) => {
                      const ChartIcon = chart.icon;
                      
                      return (
                        <span
                          key={chart.key}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-700 text-sm rounded-full border border-gray-200 group-hover:bg-blue-50 group-hover:text-blue-700 group-hover:border-blue-200 transition-colors"
                        >
                          <ChartIcon className="w-3 h-3" />
                          <span>{chart.label}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Hover Arrow */}
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <ArrowLeft className="w-4 h-4 text-white transform rotate-180" />
                  </div>
                </div>
              </button>
            );
          })}
          </div>
          </div>
        </div>
        
        {/* Author Credit - Bottom */}
        <div className="flex justify-center pb-4">
          <AuthorCredit variant="minimal" size="sm" />
        </div>
      </div>
    );
  }

  // Se categoria foi selecionada, mostrar tipos de gráfico da categoria
  const currentCategory = categories[selectedCategory];
  
  return (
    <div className="min-h-full flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" key={`chart-types-${selectedCategory}`}>
          {/* Header com navegação */}
          <div className="text-center mb-6">
        {/* Back Button - Alinhado ao título */}
        <div className="flex justify-center mb-4">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:text-blue-600 hover:border-blue-300 font-medium transition-all duration-200 shadow-sm hover:shadow-md"
            onClick={onBack}
            type="button"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
        </div>

        {/* Badge - Igual ao LeadTimeTypeSelector */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full">
            <currentCategory.icon className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800 font-medium text-sm">{currentCategory.label}</span>
          </div>
        </div>

        {/* Título da seção - Igual ao LeadTimeTypeSelector */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {selectedCategory === 'gestao-fluxo' 
              ? 'Escolha sua métrica de fluxo' 
              : 'Escolha sua ferramenta de produto'}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            {selectedCategory === 'gestao-fluxo'
              ? 'Analise o desempenho do seu processo de desenvolvimento'
              : 'Planeje e acompanhe o desenvolvimento do seu produto'}
          </p>
        </div>
      </div>

      {/* Grid de tipos de gráfico */}
      <div className={`grid gap-6 max-w-4xl mx-auto ${
        currentCategory.chartTypes.length >= 3 
          ? 'lg:grid-cols-3 md:grid-cols-2' 
          : currentCategory.chartTypes.length === 2
          ? 'md:grid-cols-2'
          : 'grid-cols-1 max-w-md'
      }`}>
        {currentCategory.chartTypes.map((chartType) => {
          const isSelected = selectedChartType === chartType.key;
          const ChartIcon = chartType.icon;
          
          return (
            <button
              key={chartType.key}
              onClick={() => {
                onChartTypeChange(chartType.key);
              }}
              className={`group relative bg-white rounded-3xl p-6 transition-all duration-300 transform hover:scale-105 border-2 text-left ${
                isSelected
                  ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:shadow-lg'
              }`}
            >
              {/* Indicador de Seleção */}
              {isSelected && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              )}

              {/* Ícone e Título na mesma linha */}
              <div className="mb-4 flex items-center gap-3">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                  chartType.key === 'leadtime' 
                    ? 'bg-blue-100' 
                    : chartType.key === 'throughput' 
                    ? 'bg-green-100'
                    : chartType.key === 'bloqueios'
                    ? 'bg-red-100'
                    : 'bg-purple-100'
                }`}>
                  <ChartIcon className={`w-6 h-6 ${
                    chartType.key === 'leadtime' 
                      ? 'text-blue-600' 
                      : chartType.key === 'throughput' 
                      ? 'text-green-600'
                      : chartType.key === 'bloqueios'
                      ? 'text-red-600'
                      : 'text-purple-600'
                  }`} />
                </div>
                
                {/* Título ao lado do ícone */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold transition-colors text-gray-900 group-hover:text-blue-600">
                    {chartType.label}
                  </h3>
                </div>
              </div>

              {/* Descrição principal */}
              <p className="text-gray-600 mb-3 leading-relaxed">
                {chartType.key === 'leadtime' 
                  ? 'Analise o tempo de ciclo dos seus itens'
                  : chartType.key === 'throughput'
                  ? 'Visualize a quantidade de entregas por período'
                  : chartType.key === 'bloqueios'
                  ? 'Identifique e analise impedimentos'
                  : chartType.description}
              </p>

              {/* Descrição secundária */}
              <p className="text-gray-500 text-sm mb-4">
                {chartType.key === 'leadtime' 
                  ? 'Scatterplot, histograma e estatísticas detalhadas'
                  : chartType.key === 'throughput'
                  ? 'Gráficos de barras e análise de tendências'
                  : chartType.key === 'bloqueios'
                  ? 'Estatísticas de impedimentos por categoria'
                  : ''}
              </p>

              {/* Seta no Hover */}
              <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <ArrowLeft className="w-4 h-4 text-white transform rotate-180" />
                </div>
              </div>
            </button>
          );
        })}
        </div>
        </div>
      </div>
    </div>
  );
};

export default CategorySelector;
